import clsx from "clsx";
import { FunctionComponent, PropsWithChildren } from "react";

import { type Authors } from "@ui/components/content-cards/content-cards.types";
import { useComponentConfig } from "@ui/context/component-config-context";
import useDefaults from "@ui/hooks/use-defaults";

import * as styles from "./ContentBlockByline.css";

export interface ContentBlockBylineBaseConfig {
  variation: "default" | "breaker";
}

interface Props extends Partial<ContentBlockBylineBaseConfig> {
  authors: Authors;
  authorByLine?: string | null;
  className?: string;
  showAuthorBy?: boolean;
  componentContext?: string;
}

const ContentBlockByline: FunctionComponent<PropsWithChildren<Props>> = ({
  authors,
  authorByLine,
  className,
  showAuthorBy = true,
  componentContext,
  ...props
}) => {
  const contentBlockBylineConfig = useComponentConfig(
    "contentBlockByline",
    componentContext || "",
  );

  const { variation } = useDefaults(props, contentBlockBylineConfig);

  if (authors.length === 0 && !authorByLine) {
    return null;
  }

  return (
    <span>
      {showAuthorBy && <span className={styles.authorBy}>By </span>}
      <span className={clsx(styles.authorText[variation], className)}>
        {authors.slice(0, 2).map((author, idx) => {
          const isLastAuthor = idx === authors.length - 1;
          return (
            <span key={idx}>
              {isLastAuthor && authors.length === 2 && (
                <span className={styles.authorBy}> and </span>
              )}
              {idx !== 0 && !isLastAuthor && authors.length > 2 && ", "}

              <span>{author.name}</span>
            </span>
          );
        })}
        {authors.length > 2 && (
          <>
            <span className={styles.authorBy}> and </span>
            <span>{`${authors.length - 2} more`}</span>
          </>
        )}
        {/** The "authorByLine" property is a string field used on LinkPosts for adding Bylines */}
        {authors.length === 0 && Boolean(authorByLine) && (
          <span>{authorByLine}</span>
        )}
      </span>
    </span>
  );
};

export default ContentBlockByline;
